// ---------------------------------------------------------------------------
// EDITABLE LOCATION SEO CONFIG — Greater Boston city hubs + service clusters
// ---------------------------------------------------------------------------
// Single source of truth for all city location pages. Edit copy here; the
// reusable CityHub.jsx and CityServicePage.jsx components render it. Routes are
// generated from this config in App.js, and sitemap entries from scripts.
//
// Hierarchy:  Home → City hub (/car-rental-<city>-ma)
//                  → Economy   (/economy-car-rental-<city>-ma)
//                  → Weekly    (/weekly-car-rental-<city>-ma)
//                  → Long-Term (/long-term-car-rental-<city>-ma)
//
// Boston is special: its hub exists, but its three service pages reuse the
// EXISTING canonical Boston URLs (no -ma duplicates). See `existingChildren`.
// Logan Airport is NOT a city hub here — its hub stays at /logan-airport-car-rental.
// ---------------------------------------------------------------------------

// Shared, intent-based framing for the three service clusters. This is legitimately
// shared search-intent copy; each page is differentiated by the city-specific
// intro/context pulled from the city config below.
export const SERVICE_META = {
  economy: {
    key: "economy",
    label: "Economy Car Rental",
    breadcrumb: "Economy Car Rental",
    slugPrefix: "economy-car-rental",
    fit: "economy",
    tagline: "Budget-friendly, fuel-efficient cars",
    useCases: [
      "Solo travelers and couples who want a compact, easy-to-park car",
      "Keeping day-to-day costs down without giving up a reliable ride",
      "Short errands and city driving on tight New England streets",
      "Business travelers who just need dependable point-to-point transport",
    ],
    intro:
      "An economy rental is the practical, wallet-friendly choice — a smaller, efficient car that's easy to maneuver and park while keeping your costs low.",
  },
  weekly: {
    key: "weekly",
    label: "Weekly Car Rental",
    breadcrumb: "Weekly Car Rental",
    slugPrefix: "weekly-car-rental",
    fit: "weekly",
    tagline: "One car for the whole week — or several",
    useCases: [
      "Extended visits and vacations where a daily rate adds up fast",
      "Temporary transportation while your own car is in the shop",
      "Work assignments, projects, and relocation weeks",
      "Family visits and longer stays that need a car on hand",
    ],
    intro:
      "A weekly rental keeps the same car with you for seven days or more, so you're not rebooking every day — ideal for longer visits, work stints, and in-between-cars situations.",
  },
  longterm: {
    key: "longterm",
    label: "Long-Term Car Rental",
    breadcrumb: "Long-Term Car Rental",
    slugPrefix: "long-term-car-rental",
    fit: "longterm",
    tagline: "Monthly and extended rentals",
    useCases: [
      "Relocation and settling-in periods before buying a car",
      "Temporary or contract work assignments in the area",
      "Extended stays and traveling professionals",
      "Drivers between vehicles who need something for a month or more",
    ],
    intro:
      "A long-term rental covers monthly and multi-week needs with one consistent vehicle, so you have reliable transportation for as long as your plans require.",
  },
};

export const SERVICE_ORDER = ["economy", "weekly", "longterm"];

// Order used for the homepage location-links section.
export const CITY_ORDER = [
  "malden", "everett", "melrose", "medford", "chelsea", "revere", "somerville",
  "saugus", "cambridge", "boston", "woburn", "lynn", "watertown", "brookline",
  "lexington", "burlington",
];

// Per-city hero photo (Boston-area / New England context). Reused on the city's
// hub page and all its service child pages. Also used as the og:image for SEO.
const IMG = (id) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=70&w=1400&auto=format&fit=crop`;

export const CITY_IMAGES = {
  malden: IMG("1516458819443-0f257d640de9"),
  everett: IMG("1764690900862-5581446fc499"),
  melrose: IMG("1782556987577-8f6efdfdf9f3"),
  medford: IMG("1611839234426-21de3ee58176"),
  chelsea: IMG("1563840111261-8b096fb63b65"),
  revere: IMG("1660063338953-38f821f107f1"),
  somerville: IMG("1556079337-a837a2d11f04"),
  saugus: IMG("1553011234-2d545e4986b0"),
  cambridge: IMG("1672613784793-91c64990806e"),
  boston: IMG("1565127803082-69dd82351360"),
  woburn: IMG("1564188537512-f6bd010d1e2a"),
  lynn: IMG("1736452866348-c30af7c2f5c1"),
  watertown: IMG("1611839234426-21de3ee58176"),
  brookline: IMG("1556079337-a837a2d11f04"),
  lexington: IMG("1782556987577-8f6efdfdf9f3"),
  burlington: IMG("1516458819443-0f257d640de9"),
};

export const cityImage = (citySlug) => CITY_IMAGES[citySlug] || CITY_IMAGES.boston;

// Shared LocalBusiness/AutoRental provider node for local-SEO structured data.
export const BELO_PROVIDER = {
  "@type": "AutoRental",
  name: "Belo Rentals",
  telephone: "+1-978-221-1451",
  url: "https://belorentals.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "235 Washington St",
    addressLocality: "Malden",
    addressRegion: "MA",
    postalCode: "02148",
    addressCountry: "US",
  },
};

// Build a Service JSON-LD node scoped to a city (and optionally a service tier).
export const cityServiceLd = (citySlug, name, serviceType = "Car Rental") => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType,
  name,
  areaServed: { "@type": "City", name: `${CITIES[citySlug].cityName}, MA` },
  provider: { ...BELO_PROVIDER, image: cityImage(citySlug) },
});

export const CITIES = {
  malden: {
    citySlug: "malden",
    cityName: "Malden",
    fit: "city",
    metaTitle: "Car Rental in Malden, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, well-maintained car in Malden, MA from Belo Rentals. Local pickup near Malden Center, easy Orange Line and Route 1 access, from $50/day. Book online.",
    keywords: "car rental Malden MA, Malden car rental, rent a car Malden",
    heroBadge: "Serving Malden & the surrounding area",
    heroCopy:
      "Belo Rentals is based in Malden, so it's our home turf. Reserve a clean, inspected car online and get on the road quickly — whether you're heading into Boston, up Route 1, or out to Logan.",
    localIntro:
      "Malden sits just north of Boston with quick access to the city, Logan Airport, and the North Shore. As a dense, transit-connected community, it's a place where people often need a car for a day, a week, or longer without the hassle of a downtown rental counter. Belo Rentals is local to Malden, which makes pickup straightforward and personal.",
    transportationContext: [
      "Malden Center is served by the MBTA Orange Line and Commuter Rail, with frequent bus connections across the area.",
      "Route 1 and Interstate 93 are close by, making trips into Boston or up to the North Shore simple.",
      "Logan Airport is a short drive away via Route 1 and the tunnels, which is handy for arriving and departing travelers.",
    ],
    localUseCases: [
      "Malden residents who need a temporary car while theirs is being repaired",
      "Visitors staying with family near Malden Center or Edgeworth",
      "Commuters who take the Orange Line but need a car for weekend trips",
      "Travelers connecting to or from Logan Airport",
    ],
    economyIntro:
      "For getting around Malden's busy streets and squeezing into street parking, a compact economy car is the easy pick — efficient, nimble, and budget-friendly.",
    weeklyIntro:
      "Staying in Malden for a week or more? A weekly rental keeps the same car with you for the whole visit instead of rebooking day by day.",
    longTermIntro:
      "New to Malden or here on assignment? A long-term monthly rental gives you dependable transportation while you settle in.",
    faqs: [
      { q: "Where do I pick up a rental car in Malden?", a: "Belo Rentals is local to Malden. Your exact pickup location and instructions are provided in your reservation once you book online." },
      { q: "Is Malden a good base for getting into Boston?", a: "Yes. Malden Center connects to Boston via the MBTA Orange Line and Commuter Rail, and Route 1 and I-93 make driving in straightforward when you'd rather have a car." },
      { q: "Can I rent a car in Malden for just a few days?", a: "Absolutely. Belo Rentals offers daily rentals from $50/day, along with weekly and monthly options if you need the car longer." },
      { q: "Do you have cars for a trip to Logan Airport from Malden?", a: "Yes. Malden is a short drive from Logan, and our fleet is well-suited for airport runs. See our Logan Airport page for airport-specific pickup and drop-off details." },
    ],
    nearbyCities: ["everett", "melrose", "medford", "revere"],
  },

  everett: {
    citySlug: "everett",
    cityName: "Everett",
    fit: "city",
    metaTitle: "Car Rental in Everett, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Everett, MA from Belo Rentals. Convenient local pickup near Encore and Route 16, from $50/day. Daily, weekly and monthly options.",
    keywords: "car rental Everett MA, Everett car rental, rent a car Everett",
    heroBadge: "Serving Everett & nearby",
    heroCopy:
      "Everett sits right next door to our Malden home base, so a Belo rental is easy to arrange. Book online and pick up a clean, well-maintained car for the city, the casino district, or a trip out of town.",
    localIntro:
      "Everett is a compact, fast-growing city between Boston, Chelsea, and Malden, anchored by the Encore Boston Harbor area and busy commercial corridors along Broadway and Route 16. With limited rapid-transit rail of its own, many Everett residents and visitors find a car the most practical way to get around and reach Boston or Logan.",
    transportationContext: [
      "Everett is served primarily by MBTA buses and the nearby Orange Line at neighboring stations, plus Silver Line connections toward the airport corridor.",
      "Route 16, Route 99, and quick access to I-93 link Everett to Boston, Somerville, and points north.",
      "The Encore Boston Harbor area draws visitors who often need flexible transportation during their stay.",
    ],
    localUseCases: [
      "Guests visiting the Encore Boston Harbor area who want their own car",
      "Everett residents needing a temporary vehicle during repairs",
      "Workers commuting to job sites around Boston and the North Shore",
      "Families hosting visitors who need a car for the week",
    ],
    economyIntro:
      "Everett's tight streets and busy parking make a compact economy car a smart, low-cost way to get around town.",
    weeklyIntro:
      "In Everett for a project, a visit, or a stay near Encore? A weekly rental keeps one car with you the whole time.",
    longTermIntro:
      "Relocating to Everett or here for a longer assignment? A monthly long-term rental keeps you mobile without a purchase.",
    faqs: [
      { q: "Where do I pick up a rental car in Everett?", a: "Belo Rentals is based next door in Malden, making Everett pickups convenient. Your exact pickup details are shared in your reservation after you book." },
      { q: "Is a car useful in Everett, or is transit enough?", a: "Everett is served mostly by buses rather than its own subway line, so many people prefer a car for flexibility — especially for trips to Boston, Logan, or the North Shore." },
      { q: "Can I rent a car near Encore Boston Harbor?", a: "Yes. Everett is close to the Encore area, and Belo Rentals can have a clean, insured car ready for your stay. Book online and we'll confirm pickup details." },
      { q: "What rental lengths do you offer in Everett?", a: "Daily rentals start from $50/day, with weekly and monthly options if you need the car for longer." },
    ],
    nearbyCities: ["malden", "chelsea", "revere", "boston"],
  },

  melrose: {
    citySlug: "melrose",
    cityName: "Melrose",
    fit: "city",
    metaTitle: "Car Rental in Melrose, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Melrose, MA from Belo Rentals. Easy local pickup, Haverhill Line commuter rail access, from $50/day. Daily, weekly, monthly.",
    keywords: "car rental Melrose MA, Melrose car rental, rent a car Melrose",
    heroBadge: "Serving Melrose & nearby towns",
    heroCopy:
      "Melrose is a quick hop from our Malden base. Reserve a clean, inspected car online for errands around town, a commute, or a weekend getaway north of the city.",
    localIntro:
      "Melrose is a friendly residential city just north of Malden, known for its walkable downtown along Main Street and easy access to the Fells. While the Commuter Rail is popular with downtown-Boston commuters, a car is often the simpler choice for errands, family trips, and getting around the North Shore.",
    transportationContext: [
      "Melrose is served by the MBTA Haverhill Line Commuter Rail, with stations including Melrose/Cedar Park and Melrose Highlands.",
      "Route 99 and connections to Route 1 and I-93 make driving to Boston or up the North Shore straightforward.",
      "The Middlesex Fells Reservation nearby is a popular outdoor destination that's easiest to reach by car.",
    ],
    localUseCases: [
      "Melrose residents needing a temporary car during repairs or between vehicles",
      "Commuters who take the train but want a car for weekends",
      "Visitors staying with family near downtown Melrose",
      "Outdoor trips to the Fells and points north",
    ],
    economyIntro:
      "For quick errands around Melrose and easy parking downtown, a compact economy car keeps things simple and affordable.",
    weeklyIntro:
      "Visiting Melrose for a week or longer? A weekly rental gives you one dependable car for the whole stay.",
    longTermIntro:
      "Settling into Melrose or here for an extended stay? A monthly rental provides steady transportation without a long-term commitment.",
    faqs: [
      { q: "Where do I pick up a rental car for Melrose?", a: "Belo Rentals is nearby in Malden, so Melrose pickups are convenient. Your exact pickup location is confirmed in your reservation." },
      { q: "How does Melrose connect to Boston?", a: "The MBTA Haverhill Line Commuter Rail runs through Melrose to Boston, and Route 99 with I-93 nearby makes driving in easy when you'd rather have a car." },
      { q: "Can I rent a car in Melrose for the weekend?", a: "Yes. Daily rentals start from $50/day — ideal for a weekend trip to the Fells or up the North Shore." },
      { q: "Do you offer longer rentals in Melrose?", a: "We do. In addition to daily rates, weekly and monthly rentals are available for longer needs." },
    ],
    nearbyCities: ["malden", "medford", "saugus", "woburn", "lynn"],
  },

  medford: {
    citySlug: "medford",
    cityName: "Medford",
    fit: "city",
    metaTitle: "Car Rental in Medford, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Medford, MA from Belo Rentals. Near Tufts, the Green Line Extension and I-93, from $50/day. Daily, weekly and monthly rentals.",
    keywords: "car rental Medford MA, Medford car rental, rent a car Medford",
    heroBadge: "Serving Medford & Tufts area",
    heroCopy:
      "Medford is minutes from our Malden home base. Book a clean, well-maintained car online for campus visits, city trips, or a drive up I-93 out of town.",
    localIntro:
      "Medford blends riverside neighborhoods, the Tufts University campus, and busy squares like Medford Square and Wellington. With the Green Line Extension and Orange Line at Wellington, it's well connected — but a car remains the go-to for campus move-ins, family visits, and trips beyond the T.",
    transportationContext: [
      "Medford is served by the MBTA Green Line Extension (Medford/Tufts) and the Orange Line at Wellington.",
      "Interstate 93 runs through Medford, giving fast access to Boston and points north.",
      "Tufts University brings students, families, and visitors who often need a car for move-in and campus visits.",
    ],
    localUseCases: [
      "Tufts students and families for move-in, move-out, and campus visits",
      "Medford residents needing a temporary vehicle during repairs",
      "Visitors exploring the Mystic River area and beyond",
      "Weekend road trips up I-93",
    ],
    economyIntro:
      "A compact economy car is easy to park around Medford Square and the Tufts area while keeping your costs low.",
    weeklyIntro:
      "Here for a week of campus visits or a longer stay? A weekly rental keeps one car with you throughout.",
    longTermIntro:
      "New to Medford or on a semester-length stay? A monthly long-term rental keeps you covered without buying a car.",
    faqs: [
      { q: "Is Belo Rentals convenient for Tufts University visits?", a: "Yes. Medford is minutes from our Malden base, which is handy for campus move-ins and family visits. Your exact pickup details come with your reservation." },
      { q: "How is Medford connected to public transit?", a: "Medford is served by the Green Line Extension (Medford/Tufts) and the Orange Line at Wellington, with I-93 running through the city for driving." },
      { q: "Can I rent a car in Medford for move-in weekend?", a: "Absolutely. A daily or weekly rental is ideal for move-in and move-out. Book online and we'll confirm pickup." },
      { q: "Do you offer monthly rentals in Medford?", a: "Yes. Along with daily and weekly rates, monthly long-term rentals are available." },
    ],
    nearbyCities: ["somerville", "malden", "everett", "melrose", "cambridge", "woburn"],
  },

  chelsea: {
    citySlug: "chelsea",
    cityName: "Chelsea",
    fit: "city",
    metaTitle: "Car Rental in Chelsea, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Chelsea, MA from Belo Rentals. Close to Logan Airport, the Silver Line SL3 and Route 1, from $50/day. Daily, weekly, monthly.",
    keywords: "car rental Chelsea MA, Chelsea car rental, rent a car Chelsea",
    heroBadge: "Serving Chelsea & the airport corridor",
    heroCopy:
      "Chelsea sits right across the water from Boston and next to Logan. Reserve a clean, insured Belo car online for airport runs, work, or getting around the city.",
    localIntro:
      "Chelsea is a dense, hard-working city directly across the Mystic River from Boston and neighboring the Logan Airport corridor. Its mix of residential streets and industrial districts makes it a practical place to keep a car, especially for airport travelers, workers, and residents managing busy schedules.",
    transportationContext: [
      "Chelsea is served by the MBTA Silver Line SL3 and Commuter Rail, connecting it to South Station and the airport area.",
      "Route 1, the Tobin Bridge, and nearby tunnels put Boston and Logan Airport within easy reach.",
      "Its location beside the Logan corridor makes it convenient for arriving and departing travelers.",
    ],
    localUseCases: [
      "Travelers connecting to and from nearby Logan Airport",
      "Chelsea residents needing a temporary car during repairs",
      "Workers commuting to job sites around Boston and the North Shore",
      "Family visits and errands across the city",
    ],
    economyIntro:
      "Chelsea's tight streets and busy parking make a compact economy car the practical, affordable choice.",
    weeklyIntro:
      "In Chelsea for a work stint or an extended visit? A weekly rental keeps one car with you all week.",
    longTermIntro:
      "Relocating to Chelsea or here on assignment? A monthly rental keeps you mobile near the city and airport.",
    faqs: [
      { q: "Is Chelsea close to Logan Airport for rentals?", a: "Yes — Chelsea neighbors the Logan Airport corridor, so it's convenient for airport travel. For airport-specific pickup and drop-off, see our Logan Airport car rental page." },
      { q: "Where do I pick up a rental car in Chelsea?", a: "Belo Rentals is based nearby in Malden. Your exact Chelsea-area pickup details are confirmed in your reservation." },
      { q: "How does Chelsea connect to Boston?", a: "The Silver Line SL3 and Commuter Rail link Chelsea to Boston, and Route 1 with the Tobin Bridge makes driving in quick." },
      { q: "What rental durations are available in Chelsea?", a: "Daily rentals start from $50/day, with weekly and monthly options for longer needs." },
    ],
    nearbyCities: ["everett", "revere", "boston", "somerville", "malden"],
  },

  revere: {
    citySlug: "revere",
    cityName: "Revere",
    fit: "city",
    metaTitle: "Car Rental in Revere, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Revere, MA from Belo Rentals. Near Revere Beach, the Blue Line and Route 1, close to Logan. From $50/day. Daily, weekly, monthly.",
    keywords: "car rental Revere MA, Revere car rental, rent a car Revere",
    heroBadge: "Serving Revere & the North Shore edge",
    heroCopy:
      "Revere is home to America's first public beach and sits close to Logan. Book a clean, insured Belo car online for beach days, airport runs, or getting around the North Shore.",
    localIntro:
      "Revere combines a lively beachfront along Revere Beach with residential neighborhoods and easy highway access. Close to both Logan Airport and the North Shore, it's a place where a car makes beach trips, airport connections, and everyday errands much simpler.",
    transportationContext: [
      "Revere is served by the MBTA Blue Line, with stations including Revere Beach and Wonderland.",
      "Route 1, Route 1A, and Route 16 connect Revere to Boston, Logan Airport, and the North Shore.",
      "Revere Beach is a popular seasonal destination that's easiest to enjoy with your own car.",
    ],
    localUseCases: [
      "Beachgoers heading to Revere Beach and the North Shore",
      "Travelers connecting to nearby Logan Airport",
      "Revere residents needing a temporary vehicle",
      "Visitors staying near the beach for the week",
    ],
    economyIntro:
      "For beach runs and quick trips around Revere, a compact economy car is easy to park and light on gas.",
    weeklyIntro:
      "Spending a week near Revere Beach or visiting family? A weekly rental keeps one car with you the whole time.",
    longTermIntro:
      "Settling into Revere or here on a longer stay? A monthly rental provides steady, flexible transportation.",
    faqs: [
      { q: "Is Revere close to Logan Airport?", a: "Yes. Revere is near the Logan Airport corridor via Route 1A and Route 16. For airport-specific pickup and drop-off, see our Logan Airport page." },
      { q: "Can I rent a car for Revere Beach?", a: "Absolutely. A daily or weekly rental makes beach days and North Shore trips easy. Book online and we'll confirm pickup." },
      { q: "How is Revere connected to transit?", a: "The MBTA Blue Line serves Revere at stations like Revere Beach and Wonderland, with Route 1 and Route 1A for driving." },
      { q: "What rental lengths do you offer in Revere?", a: "Daily rentals start from $50/day, along with weekly and monthly options." },
    ],
    nearbyCities: ["chelsea", "lynn", "boston"],
  },

  somerville: {
    citySlug: "somerville",
    cityName: "Somerville",
    fit: "city",
    metaTitle: "Car Rental in Somerville, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Somerville, MA from Belo Rentals. Near Davis and Assembly, the Red Line and Green Line Extension, from $50/day. Daily to monthly.",
    keywords: "car rental Somerville MA, Somerville car rental, rent a car Somerville",
    heroBadge: "Serving Somerville & the squares",
    heroCopy:
      "From Davis to Assembly Row, Somerville is dense and lively. Reserve a clean, insured Belo car online for moves, road trips, and everything the T doesn't reach.",
    localIntro:
      "Somerville is one of the most densely populated cities in New England, full of vibrant squares like Davis, Union, and Assembly Row. It's extremely transit-friendly, yet its tight parking and busy streets mean residents and visitors often rent a car specifically for moves, errands, and trips out of town.",
    transportationContext: [
      "Somerville is served by the MBTA Red Line at Davis and the Green Line Extension through Union Square and Somerville.",
      "Interstate 93 and McGrath Highway provide driving access to Boston and points north.",
      "Assembly Row is a popular shopping and dining destination with heavy visitor traffic.",
    ],
    localUseCases: [
      "Somerville residents handling apartment moves and big-box runs",
      "Students and young professionals needing a car for weekend trips",
      "Visitors exploring Davis Square and Assembly Row",
      "Temporary transportation while a personal car is unavailable",
    ],
    economyIntro:
      "With Somerville's famously tight parking, a compact economy car is the smart, easy-to-squeeze-in choice.",
    weeklyIntro:
      "In Somerville for a week or juggling a move? A weekly rental keeps one car with you throughout.",
    longTermIntro:
      "New to Somerville or between cars? A monthly long-term rental keeps you covered without a purchase.",
    faqs: [
      { q: "Is a car worth it in transit-friendly Somerville?", a: "Many people use the T day to day but rent for moves, errands, and trips out of the city where a car is far more convenient. Daily, weekly, and monthly options are available." },
      { q: "Where do I pick up a rental car for Somerville?", a: "Belo Rentals is nearby in Malden. Your exact pickup details are provided in your reservation after booking online." },
      { q: "How does Somerville connect to Boston?", a: "The Red Line at Davis and the Green Line Extension serve Somerville, with I-93 and McGrath Highway for driving." },
      { q: "Can I rent a car for a move in Somerville?", a: "Yes. A daily or weekly rental is well-suited to apartment moves and big-box trips. Note that all vehicles are standard passenger cars, not moving trucks." },
    ],
    nearbyCities: ["cambridge", "medford", "boston", "everett", "malden"],
  },

  saugus: {
    citySlug: "saugus",
    cityName: "Saugus",
    fit: "city",
    metaTitle: "Car Rental in Saugus, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Saugus, MA from Belo Rentals. Right off Route 1 with easy North Shore access, from $50/day. Daily, weekly and monthly rentals.",
    keywords: "car rental Saugus MA, Saugus car rental, rent a car Saugus",
    heroBadge: "Serving Saugus & Route 1",
    heroCopy:
      "Saugus lives along the Route 1 corridor, where a car is essential. Book a clean, insured Belo rental online for shopping, commuting, or a North Shore road trip.",
    localIntro:
      "Saugus is a suburban North Shore community defined by the bustling Route 1 retail corridor and its historic Saugus Iron Works. Without rapid-transit rail of its own, Saugus is a car-first town where renting is often the most practical way to shop, commute, and travel.",
    transportationContext: [
      "Saugus is served primarily by MBTA buses and does not have its own subway or commuter-rail station.",
      "Route 1 runs directly through Saugus, with Route 99 and I-95 nearby for regional trips.",
      "The Route 1 corridor is a major shopping destination that draws steady traffic.",
    ],
    localUseCases: [
      "Shoppers and diners along the Route 1 corridor",
      "Saugus residents needing a temporary car during repairs",
      "Commuters heading into Boston or around the North Shore",
      "Visitors staying with family who need a car for the week",
    ],
    economyIntro:
      "For everyday Route 1 errands and commuting, an economy car keeps fuel and rental costs down.",
    weeklyIntro:
      "Visiting Saugus for a week or more? A weekly rental keeps one car with you for the whole stay.",
    longTermIntro:
      "Relocating to Saugus or here on assignment? A monthly rental gives you reliable transportation in this car-first town.",
    faqs: [
      { q: "Do I need a car in Saugus?", a: "Because Saugus doesn't have its own subway or commuter-rail station, a car is usually the most practical way to get around and reach Boston or the North Shore." },
      { q: "Where do I pick up a rental in Saugus?", a: "Belo Rentals is based nearby in Malden. Your exact pickup location is confirmed in your reservation." },
      { q: "Is Saugus convenient to Route 1 shopping?", a: "Yes. Route 1 runs right through Saugus, so a rental makes shopping trips and errands easy." },
      { q: "What rental lengths do you offer in Saugus?", a: "Daily rentals start from $50/day, with weekly and monthly options for longer needs." },
    ],
    nearbyCities: ["melrose", "revere", "lynn", "malden", "woburn"],
  },

  cambridge: {
    citySlug: "cambridge",
    cityName: "Cambridge",
    fit: "city",
    metaTitle: "Car Rental in Cambridge, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Cambridge, MA from Belo Rentals. Near Harvard, MIT and the Red Line, from $50/day. Daily, weekly and monthly rentals available.",
    keywords: "car rental Cambridge MA, Cambridge car rental, rent a car Cambridge",
    heroBadge: "Serving Cambridge, Harvard & MIT",
    heroCopy:
      "Home to Harvard and MIT, Cambridge is busy and parking-tight. Reserve a clean, insured Belo car online for campus visits, moves, and trips beyond the Red Line.",
    localIntro:
      "Cambridge is an academic and innovation hub across the river from Boston, home to Harvard and MIT and a steady stream of students, researchers, and visitors. It's very transit-rich, but campus move-ins, family visits, and weekend trips are exactly when a rental car earns its keep.",
    transportationContext: [
      "Cambridge is served by the MBTA Red Line at Harvard, Central, Kendall/MIT, and Porter.",
      "Memorial Drive, the river crossings, and nearby I-93 connect Cambridge to Boston and beyond.",
      "Harvard and MIT bring heavy visitor and family traffic, especially around move-in and graduation.",
    ],
    localUseCases: [
      "Harvard and MIT students and families for move-in and campus visits",
      "Researchers and visiting academics needing a car for a stay",
      "Cambridge residents handling moves and errands",
      "Weekend and out-of-town trips beyond the Red Line",
    ],
    economyIntro:
      "In Cambridge, where parking is at a premium, a compact economy car is the easiest and most affordable option.",
    weeklyIntro:
      "Here for a week of campus visits or research? A weekly rental keeps one car with you throughout.",
    longTermIntro:
      "Relocating to Cambridge or on a semester assignment? A monthly rental keeps you mobile without buying a car.",
    faqs: [
      { q: "Is Belo Rentals good for Harvard or MIT visits?", a: "Yes. A rental is handy for move-in, graduation, and family visits when parking and logistics matter. Your pickup details come with your reservation." },
      { q: "How is Cambridge connected to Boston?", a: "The Red Line links Cambridge to Boston at Harvard, Central, Kendall/MIT, and Porter, with Memorial Drive and river crossings for driving." },
      { q: "Can I rent a car in Cambridge for the weekend?", a: "Absolutely. Daily rentals start from $50/day, with weekly options for longer visits." },
      { q: "Do you offer monthly rentals in Cambridge?", a: "Yes. Along with daily and weekly rates, monthly long-term rentals are available." },
    ],
    nearbyCities: ["somerville", "boston", "watertown", "medford", "brookline"],
  },

  boston: {
    citySlug: "boston",
    cityName: "Boston",
    fit: "city",
    // Boston reuses existing canonical child pages — no -ma duplicates.
    existingChildren: {
      economy: { url: "/economy-car-rental-boston", label: "Economy Car Rental Boston" },
      weekly: { url: "/boston-weekly-car-rental", label: "Weekly Car Rental Boston" },
      longterm: { url: "/long-term-car-rental-boston", label: "Long-Term Car Rental Boston" },
    },
    metaTitle: "Car Rental in Boston, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, well-maintained car in Boston, MA from Belo Rentals. A local alternative to the counter brands, with Logan Airport pickup and rates from $50/day.",
    keywords: "car rental Boston MA, Boston car rental, rent a car Boston",
    heroBadge: "Car rental across Boston",
    heroCopy:
      "Skip the counter lines. Belo Rentals is a local team offering clean, well-maintained cars across Boston, with easy online booking and optional Logan Airport pickup — from $50/day.",
    localIntro:
      "Boston is the hub of New England — a compact, historic city where driving downtown is tight but a car unlocks the whole region, from the North Shore to Cape Cod. Belo Rentals gives you a straightforward, local alternative to the big rental counters, with transparent booking and cars ready when you need them.",
    transportationContext: [
      "Boston is served by the MBTA subway, bus, and Commuter Rail network across the city and suburbs.",
      "Logan International Airport (BOS) is minutes from downtown via the tunnels and Route 1A.",
      "Interstates 90 (Mass Pike) and 93 connect Boston to the rest of Massachusetts and New England.",
    ],
    localUseCases: [
      "Visitors who want to explore beyond the city on their own schedule",
      "Travelers arriving at Logan who need a car for their stay",
      "Residents needing a temporary vehicle or a weekend car",
      "Day trips to Cape Cod, Salem, Plymouth, and the North Shore",
    ],
    economyIntro:
      "For getting around Boston's narrow streets and keeping costs down, our economy options are the easy pick.",
    weeklyIntro:
      "Visiting Boston for a week or more? Weekly pricing keeps one car with you for the whole stay.",
    longTermIntro:
      "Relocating to Boston or here for an extended assignment? Long-term monthly rentals keep you covered.",
    faqs: [
      { q: "How is Belo Rentals different from the big rental brands in Boston?", a: "Belo Rentals is a local team offering clean, well-maintained cars with simple online booking and optional Logan Airport pickup — a straightforward alternative to crowded rental counters." },
      { q: "Can I get a rental car from Logan Airport?", a: "Yes. Belo Rentals offers Logan Airport pickup and drop-off. See our dedicated Logan Airport car rental page for airport-specific directions and details." },
      { q: "Do I need a car if I'm staying in downtown Boston?", a: "Downtown is walkable and transit-rich, but a car is invaluable for day trips to the coast, the North Shore, and beyond. Daily, weekly, and monthly rentals are available." },
      { q: "What does it cost to rent a car in Boston?", a: "Rates start from $50/day, with weekly and monthly options for longer stays. You'll see current pricing when you book online." },
    ],
    nearbyCities: ["cambridge", "somerville", "brookline", "watertown"],
  },

  woburn: {
    citySlug: "woburn",
    cityName: "Woburn",
    fit: "longterm",
    metaTitle: "Car Rental in Woburn, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Woburn, MA from Belo Rentals. At the I-93/I-95 crossroads with commuter rail access, from $50/day. Daily, weekly and monthly.",
    keywords: "car rental Woburn MA, Woburn car rental, rent a car Woburn",
    heroBadge: "Serving Woburn & the 93/95 crossroads",
    heroCopy:
      "Woburn sits where I-93 meets I-95, a natural base for regional driving. Book a clean, insured Belo car online for work, commuting, or a trip in any direction.",
    localIntro:
      "Woburn is a well-connected suburb north of Boston at the crossroads of Interstates 93 and 95, with a mix of neighborhoods, offices, and the Anderson Regional Transportation Center. Its highway access makes it a practical base for commuters, business travelers, and anyone who needs to cover ground across the region.",
    transportationContext: [
      "Woburn is served by the MBTA Lowell Line at Anderson/Woburn Regional Transportation Center.",
      "The junction of I-93 and I-95 (Route 128) gives Woburn exceptional access in every direction.",
      "Nearby office parks and business districts generate steady demand for flexible transportation.",
    ],
    localUseCases: [
      "Business travelers visiting office parks along Route 128",
      "Commuters using Anderson/Woburn who need a car regionally",
      "Woburn residents needing a temporary vehicle",
      "Longer stays and work assignments in the area",
    ],
    economyIntro:
      "For efficient commuting and errands around Woburn, an economy car keeps fuel and costs low on all that highway driving.",
    weeklyIntro:
      "In Woburn for a work project or an extended visit? A weekly rental keeps one car with you all week.",
    longTermIntro:
      "On a longer assignment near Route 128? A monthly long-term rental is a dependable, flexible option.",
    faqs: [
      { q: "Why is Woburn a convenient base for driving?", a: "Woburn sits at the junction of I-93 and I-95 (Route 128), giving quick access to Boston, the North Shore, and the suburbs — ideal if you'll be covering ground." },
      { q: "Is there commuter rail in Woburn?", a: "Yes. The MBTA Lowell Line serves the Anderson/Woburn Regional Transportation Center, which also connects to regional bus service." },
      { q: "Where do I pick up a rental in Woburn?", a: "Belo Rentals is based nearby in Malden. Your exact pickup details are confirmed in your reservation." },
      { q: "Do you offer monthly rentals in Woburn?", a: "Yes. Daily, weekly, and monthly rentals are all available for stays of any length." },
    ],
    nearbyCities: ["burlington", "lexington", "medford", "melrose", "saugus"],
  },

  lynn: {
    citySlug: "lynn",
    cityName: "Lynn",
    fit: "city",
    metaTitle: "Car Rental in Lynn, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Lynn, MA from Belo Rentals. On the North Shore with commuter rail and Route 1A access, from $50/day. Daily, weekly and monthly.",
    keywords: "car rental Lynn MA, Lynn car rental, rent a car Lynn",
    heroBadge: "Serving Lynn & the North Shore",
    heroCopy:
      "Lynn anchors the North Shore with its coastline and commuter rail. Reserve a clean, insured Belo car online for the beach, commuting, or exploring Cape Ann.",
    localIntro:
      "Lynn is a large, diverse North Shore city with a working waterfront, the scenic Lynn Shore Drive, and easy reach to Nahant and Cape Ann. With Commuter Rail service and Route 1A running through, Lynn is a practical spot to rent a car for coastal drives, commuting, and everyday needs.",
    transportationContext: [
      "Lynn is served by the MBTA Newburyport/Rockport Commuter Rail line at Lynn and River Works stations.",
      "Route 1A and the Lynnway connect Lynn to Boston, Logan Airport, and the North Shore.",
      "Lynn Shore Drive and nearby Nahant make for popular coastal drives.",
    ],
    localUseCases: [
      "Coastal trips along Lynn Shore Drive, Nahant, and Cape Ann",
      "Lynn residents needing a temporary car during repairs",
      "Commuters who take the train but want a car for errands",
      "Visitors staying on the North Shore for the week",
    ],
    economyIntro:
      "For everyday driving around Lynn and easy parking, a compact economy car keeps costs low.",
    weeklyIntro:
      "Spending a week on the North Shore? A weekly rental keeps one car with you for the whole visit.",
    longTermIntro:
      "Relocating to Lynn or here on assignment? A monthly rental provides steady, flexible transportation.",
    faqs: [
      { q: "How is Lynn connected to Boston?", a: "The MBTA Newburyport/Rockport Commuter Rail serves Lynn, and Route 1A with the Lynnway makes driving to Boston and Logan straightforward." },
      { q: "Can I rent a car in Lynn for coastal trips?", a: "Yes. A rental is perfect for Lynn Shore Drive, Nahant, and Cape Ann. Daily rentals start from $50/day." },
      { q: "Where do I pick up a rental in Lynn?", a: "Belo Rentals is based nearby in Malden. Your exact pickup location is confirmed in your reservation." },
      { q: "What rental lengths are available in Lynn?", a: "Daily, weekly, and monthly rentals are all available depending on how long you need the car." },
    ],
    nearbyCities: ["revere", "saugus", "melrose", "malden", "chelsea"],
  },

  watertown: {
    citySlug: "watertown",
    cityName: "Watertown",
    fit: "city",
    metaTitle: "Car Rental in Watertown, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Watertown, MA from Belo Rentals. Along the Charles River with quick Cambridge and Route 16 access, from $50/day. Daily to monthly.",
    keywords: "car rental Watertown MA, Watertown car rental, rent a car Watertown",
    heroBadge: "Serving Watertown & the Charles",
    heroCopy:
      "Watertown hugs the Charles River just west of Cambridge. Book a clean, insured Belo car online for commuting, errands, or a trip out of the city.",
    localIntro:
      "Watertown is a compact riverside city west of Cambridge, known for Watertown Square, the Charles River paths, and a growing life-sciences presence along Arsenal Street. It's served mainly by buses rather than the subway, so a car is often the simplest way to get around and reach the wider region.",
    transportationContext: [
      "Watertown is served primarily by MBTA buses, including routes into Harvard Square and Boston.",
      "Route 16, Arsenal Street, and nearby connections link Watertown to Cambridge, Boston, and the Mass Pike.",
      "The Charles River path is a popular recreational corridor along the city's edge.",
    ],
    localUseCases: [
      "Watertown residents commuting where buses alone aren't enough",
      "Workers at Arsenal Street offices and life-sciences campuses",
      "Visitors exploring Cambridge, Boston, and the river paths",
      "Temporary transportation during vehicle repairs",
    ],
    economyIntro:
      "For getting around Watertown Square and nearby parking, a compact economy car is the practical, low-cost pick.",
    weeklyIntro:
      "In Watertown for a work stint or an extended stay? A weekly rental keeps one car with you throughout.",
    longTermIntro:
      "Relocating to Watertown or on a longer assignment? A monthly rental keeps you mobile without a purchase.",
    faqs: [
      { q: "Is a car useful in Watertown?", a: "Watertown is served mostly by buses rather than the subway, so many residents and visitors prefer a car for flexibility around town and into the region." },
      { q: "How does Watertown connect to Cambridge and Boston?", a: "Frequent MBTA buses run to Harvard Square and Boston, and Route 16 with Arsenal Street provides direct driving routes." },
      { q: "Where do I pick up a rental in Watertown?", a: "Belo Rentals is based nearby in Malden. Your exact pickup details are shared in your reservation." },
      { q: "Do you offer weekly and monthly rentals in Watertown?", a: "Yes. Along with daily rates from $50/day, weekly and monthly rentals are available." },
    ],
    nearbyCities: ["cambridge", "brookline", "boston", "lexington", "somerville"],
  },

  brookline: {
    citySlug: "brookline",
    cityName: "Brookline",
    fit: "city",
    metaTitle: "Car Rental in Brookline, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Brookline, MA from Belo Rentals. Near Coolidge Corner, the Green Line C and D branches, from $50/day. Daily, weekly and monthly.",
    keywords: "car rental Brookline MA, Brookline car rental, rent a car Brookline",
    heroBadge: "Serving Brookline & Coolidge Corner",
    heroCopy:
      "Brookline wraps around Boston with leafy streets and lively squares. Reserve a clean, insured Belo car online for errands, medical trips, or a weekend away.",
    localIntro:
      "Brookline is an affluent, walkable town nearly surrounded by Boston, centered on Coolidge Corner and Brookline Village and close to the Longwood Medical Area. It's very transit-friendly via the Green Line, yet residents and visitors frequently rent a car for medical appointments, errands, and trips beyond the trolley lines.",
    transportationContext: [
      "Brookline is served by the MBTA Green Line C branch (Beacon Street) and D branch (Riverside).",
      "Route 9 and nearby connections link Brookline to Boston, the Longwood Medical Area, and the Mass Pike.",
      "The nearby Longwood Medical Area draws patients, families, and staff who often need a car.",
    ],
    localUseCases: [
      "Patients and families visiting the nearby Longwood Medical Area",
      "Brookline residents running errands beyond the Green Line",
      "Visitors staying near Coolidge Corner for the week",
      "Weekend trips out of the city",
    ],
    economyIntro:
      "For Brookline's tree-lined streets and tight parking, a compact economy car is the easy, affordable option.",
    weeklyIntro:
      "Staying in Brookline for a week or supporting a medical visit? A weekly rental keeps one car with you throughout.",
    longTermIntro:
      "Here for extended care, work, or a move? A monthly long-term rental provides steady transportation.",
    faqs: [
      { q: "Is Brookline convenient to the Longwood Medical Area?", a: "Yes. Brookline is close to the Longwood Medical Area, and a rental can make appointments and family support much easier. Your pickup details come with your reservation." },
      { q: "How is Brookline connected to Boston?", a: "The Green Line C and D branches serve Brookline, with Route 9 providing a direct driving route into Boston and Longwood." },
      { q: "Can I rent a car in Brookline for a few days?", a: "Absolutely. Daily rentals start from $50/day, with weekly and monthly options for longer needs." },
      { q: "Where do I pick up a rental in Brookline?", a: "Belo Rentals is based nearby in Malden. Your exact pickup location is confirmed in your reservation." },
    ],
    nearbyCities: ["boston", "cambridge", "watertown", "somerville"],
  },

  lexington: {
    citySlug: "lexington",
    cityName: "Lexington",
    fit: "longterm",
    metaTitle: "Car Rental in Lexington, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Lexington, MA from Belo Rentals. Historic Battle Road town near Route 128/I-95, from $50/day. Daily, weekly and monthly rentals.",
    keywords: "car rental Lexington MA, Lexington car rental, rent a car Lexington",
    heroBadge: "Serving historic Lexington",
    heroCopy:
      "Lexington is where the Revolution began, and it's still best explored by car. Book a clean, insured Belo car online for history tours, business trips, or family visits.",
    localIntro:
      "Lexington is a historic, leafy suburb northwest of Boston, famous as the site of the first battle of the American Revolution and home to Battle Road and the Minute Man National Historical Park. Without its own subway or commuter-rail station, Lexington is a car-oriented town where renting is the natural way to explore and get around.",
    transportationContext: [
      "Lexington is served by MBTA buses and does not have its own subway or commuter-rail station.",
      "Route 128 (I-95) and Route 2 nearby connect Lexington to Boston, Cambridge, and the suburbs.",
      "The Minute Man National Historical Park and Battle Road draw history-minded visitors year-round.",
    ],
    localUseCases: [
      "History travelers visiting Battle Road and the Minute Man park",
      "Business visitors to the Route 128 technology corridor",
      "Lexington residents needing a temporary vehicle",
      "Family visits and longer stays in the suburbs",
    ],
    economyIntro:
      "For touring Lexington's historic sites and easy parking, an economy car is a comfortable, low-cost choice.",
    weeklyIntro:
      "Visiting Lexington for a week of history or work? A weekly rental keeps one car with you throughout.",
    longTermIntro:
      "On a longer assignment near the Route 128 corridor? A monthly rental is a dependable, flexible option.",
    faqs: [
      { q: "Do I need a car to visit Lexington?", a: "Because Lexington doesn't have its own subway or commuter-rail station, a car is the most practical way to reach Battle Road, the Minute Man park, and the surrounding area." },
      { q: "Is Lexington convenient to the Route 128 tech corridor?", a: "Yes. Route 128 (I-95) is nearby, making Lexington a handy base for business visits along the corridor." },
      { q: "Where do I pick up a rental for Lexington?", a: "Belo Rentals is based in nearby Malden. Your exact pickup details are provided in your reservation." },
      { q: "Do you offer weekly and monthly rentals in Lexington?", a: "Yes. Daily rentals start from $50/day, with weekly and monthly options for longer stays." },
    ],
    nearbyCities: ["burlington", "woburn", "watertown", "cambridge", "medford"],
  },

  burlington: {
    citySlug: "burlington",
    cityName: "Burlington",
    fit: "longterm",
    metaTitle: "Car Rental in Burlington, MA | Belo Rentals",
    metaDescription:
      "Rent a clean, insured car in Burlington, MA from Belo Rentals. Near the Burlington Mall and the I-95/Route 3 hub, from $50/day. Daily, weekly and monthly.",
    keywords: "car rental Burlington MA, Burlington car rental, rent a car Burlington",
    heroBadge: "Serving Burlington & the shopping district",
    heroCopy:
      "Burlington is a shopping and business hub off I-95. Reserve a clean, insured Belo car online for retail trips, office visits, or a longer stay in the suburbs.",
    localIntro:
      "Burlington is a busy commercial suburb northwest of Boston, anchored by the Burlington Mall, the 3rd Ave district, and a dense cluster of corporate offices and hotels. Positioned at the meeting of I-95 (Route 128) and Route 3, it's a car-first town where renting is the easiest way to shop, work, and travel the region.",
    transportationContext: [
      "Burlington is served by MBTA and local buses and does not have its own subway or commuter-rail station.",
      "The junction of I-95 (Route 128) and Route 3 gives Burlington strong access in every direction.",
      "The Burlington Mall and surrounding business parks generate steady visitor and commuter traffic.",
    ],
    localUseCases: [
      "Shoppers heading to the Burlington Mall and 3rd Ave",
      "Business travelers visiting nearby corporate campuses and hotels",
      "Burlington residents needing a temporary vehicle",
      "Extended work assignments and longer stays",
    ],
    economyIntro:
      "For shopping runs and errands around Burlington, an economy car keeps fuel and rental costs low.",
    weeklyIntro:
      "In Burlington for a work project or an extended visit? A weekly rental keeps one car with you all week.",
    longTermIntro:
      "On a longer corporate assignment in Burlington? A monthly long-term rental is a reliable, flexible option.",
    faqs: [
      { q: "Do I need a car in Burlington?", a: "Burlington doesn't have its own subway or commuter-rail station, so a car is the most practical way to reach the mall, offices, and the wider region." },
      { q: "Is Burlington good for business travel?", a: "Yes. With corporate campuses and hotels near the I-95/Route 3 junction, a rental makes getting between meetings and your hotel easy." },
      { q: "Where do I pick up a rental for Burlington?", a: "Belo Rentals is based in nearby Malden. Your exact pickup details are confirmed in your reservation." },
      { q: "Do you offer monthly rentals in Burlington?", a: "Yes. Daily, weekly, and monthly rentals are all available for stays of any length." },
    ],
    nearbyCities: ["woburn", "lexington", "medford", "melrose"],
  },
};

// ---- helpers -------------------------------------------------------------
export const hubSlug = (citySlug) => `car-rental-${citySlug}-ma`;

export const serviceSlug = (citySlug, service) =>
  `${SERVICE_META[service].slugPrefix}-${citySlug}-ma`;

// Resolve a child page URL for a city + service, honoring Boston's existing pages.
export const childUrl = (city, service) => {
  const cfg = CITIES[city];
  if (cfg.existingChildren && cfg.existingChildren[service]) return cfg.existingChildren[service].url;
  return `/${serviceSlug(city, service)}`;
};

export const childLabel = (city, service) => {
  const cfg = CITIES[city];
  if (cfg.existingChildren && cfg.existingChildren[service]) return cfg.existingChildren[service].label;
  return `${SERVICE_META[service].label} in ${cfg.cityName}`;
};

// All standard-city service slugs to generate routes/sitemap (excludes Boston's existing children).
export const generatedServiceSlugs = () => {
  const out = [];
  Object.values(CITIES).forEach((c) => {
    if (c.existingChildren) return; // Boston uses existing canonical URLs
    SERVICE_ORDER.forEach((s) => out.push({ citySlug: c.citySlug, service: s, slug: serviceSlug(c.citySlug, s) }));
  });
  return out;
};

// ---- service-page content helpers ----------------------------------------
// The city config stores one intro line per service (economyIntro / weeklyIntro /
// longTermIntro). These helpers turn that into unique, non-thin service pages —
// meta, keywords and a small city+service FAQ set generated from the config.
const SERVICE_INTRO_KEY = { economy: "economyIntro", weekly: "weeklyIntro", longterm: "longTermIntro" };

export const serviceIntro = (city, service) => {
  const cfg = CITIES[city];
  return cfg[SERVICE_INTRO_KEY[service]] || SERVICE_META[service].intro;
};

export const serviceMetaTitle = (city, service) =>
  `${SERVICE_META[service].label} in ${CITIES[city].cityName}, MA | Belo Rentals`;

export const serviceMetaDescription = (city, service) => {
  const name = CITIES[city].cityName;
  const map = {
    economy: `Affordable economy car rental in ${name}, MA from Belo Rentals — clean, fuel-efficient, well-maintained cars from $50/day. Easy local pickup. Book online.`,
    weekly: `Weekly car rental in ${name}, MA from Belo Rentals. Keep one clean, insured car for the whole week from $50/day, with an unlimited-mile option. Book online.`,
    longterm: `Long-term & monthly car rental in ${name}, MA from Belo Rentals. Reliable, insured transportation from $50/day with weekly value pricing. Book online.`,
  };
  return map[service];
};

export const serviceKeywords = (city, service) => {
  const name = CITIES[city].cityName;
  const map = {
    economy: `economy car rental ${name} MA, cheap car rental ${name}, compact car rental ${name}`,
    weekly: `weekly car rental ${name} MA, car rental by the week ${name}, weekly car hire ${name}`,
    longterm: `long term car rental ${name} MA, monthly car rental ${name}, extended car rental ${name}`,
  };
  return map[service];
};

export const serviceFaqs = (city, service) => {
  const name = CITIES[city].cityName;
  const intro = serviceIntro(city, service);
  const map = {
    economy: [
      { q: `How much is an economy car rental in ${name}?`, a: `Economy rentals with Belo Rentals start from $50/day in ${name} — our most budget-friendly category, with weekly pricing for longer stays.` },
      { q: `Is a compact car a good fit for ${name}?`, a: intro },
      { q: `Where do I pick up an economy rental in ${name}?`, a: `Belo Rentals is based nearby in Malden, so ${name} pickups are convenient. Your exact pickup details are confirmed in your reservation after you book online.` },
      { q: `How many people fit in an economy car?`, a: `Most economy cars seat four to five comfortably. Traveling with a group or extra luggage around ${name}? Ask us about a sedan or SUV instead.` },
    ],
    weekly: [
      { q: `How much is a weekly car rental in ${name}?`, a: `Weekly rentals start from $50/day, with pricing that rewards longer stays — better value than booking day by day in ${name}.` },
      { q: `Why choose a weekly rental in ${name}?`, a: intro },
      { q: `Can I extend my weekly rental in ${name}?`, a: `Yes — just reach out before your return date and we'll extend it, subject to availability.` },
      { q: `Is there a mileage limit on weekly rentals?`, a: `Standard mileage is included, and you can add unlimited miles for $10/day if you plan longer drives around ${name} and beyond.` },
    ],
    longterm: [
      { q: `Can I rent a car for a month in ${name}?`, a: `Yes — Belo Rentals offers long-term and monthly rentals in ${name} with weekly value pricing. Contact us for a tailored quote on longer terms.` },
      { q: `Who is a long-term rental in ${name} best for?`, a: intro },
      { q: `How does long-term rental pricing work?`, a: `It starts from $50/day, and longer commitments unlock better weekly rates. Reach out and we'll tailor a monthly quote for your ${name} stay.` },
      { q: `How does protection work on long-term rentals?`, a: `Protection details are shown before you pay — see your rental agreement for exact coverage specifics for the length of your term.` },
    ],
  };
  return map[service];
};
